import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock, toDisplayString as _toDisplayString, resolveDynamicComponent as _resolveDynamicComponent, vShow as _vShow, withDirectives as _withDirectives, normalizeClass as _normalizeClass, Fragment as _Fragment, withModifiers as _withModifiers, resolveComponent as _resolveComponent } from "vue"
import _imports_0 from '@/assets/logo_archimede.svg'


const _hoisted_1 = {
  key: 0,
  class: "tour-overlay"
}
const _hoisted_2 = { class: "hdr" }
const _hoisted_3 = ["title"]
const _hoisted_4 = { class: "menu" }
const _hoisted_5 = { class: "nav-row" }
const _hoisted_6 = {
  key: 0,
  class: "sub"
}
const _hoisted_7 = ["disabled", "title"]
const _hoisted_8 = ["title"]

import axios from "@/axiosInstance";
import router, { authIsCached, setAuth } from "@/router";
import {
	AlertTriangle as AlertTriangleIcon,
	ChevronRight as ChevronIcon,
	CircleUser as CircleUserIcon,
	ClipboardMinus as ReportIcon,
	Cpu as CpuIcon,
	Database as DatabaseIcon,
	Edit as EditIcon,
	Globe as GlobeIcon,
	HelpCircle as HelpCircleIcon,
	Home as HomeIcon,
	LogOut as LogOutIcon,
	Map as MapIcon,
} from "lucide-vue-next";
import {
	computed,
	inject,
	markRaw,
	nextTick,
	onBeforeUnmount,
	onMounted,
	ref,
	watch,
} from "vue";
import { RouterLink, useRoute } from "vue-router";
import { useToast } from "vue-toast-notification";
import draggable from "vuedraggable";
import DevicePickerModal from "./DevicePickerModal.vue";
import NotificationBell from "./NotificationBell.vue";

const LS_ORDER_KEY = "sidebar-nav-order";

const PAGE = "Sidebar";


export default /*@__PURE__*/_defineComponent({
  __name: 'SideBar',
  props: {
    isOpen: { type: Boolean },
    desktop: { type: Boolean }
  },
  emits: ["toggle"],
  setup(__props: any) {

const props = __props;


/* ---- nav items riordinabili ---- */
const ALL_NAV_ITEMS = [
	{ key: "dashboard", to: "/dashboard", icon: markRaw(HomeIcon), label: "Dashboard", cls: "ico-home" },
	{ key: "devices", to: "/devices", icon: markRaw(CpuIcon), label: "Devices", cls: "ico-devices" },
	{ key: "live-maps", to: "/live-maps", icon: markRaw(MapIcon), label: "Live Maps", cls: "ico-live-maps" },
	{ key: "alerts", to: "/alerts", icon: markRaw(AlertTriangleIcon), label: "Alert", cls: "ico-alerts" },
	{ key: "reports", to: "/reports", icon: markRaw(ReportIcon), label: "Reports", cls: "ico-reports" },
	{ key: "external-services", to: "/external-services", icon: markRaw(GlobeIcon), label: "Web Services", cls: "ico-services" },
];

function loadNavOrder() {
	try {
		const stored = localStorage.getItem(LS_ORDER_KEY);
		if (stored) {
			const keys = JSON.parse(stored) as string[];
			const allKeys = ALL_NAV_ITEMS.map((i) => i.key);
			if (keys.length === allKeys.length && allKeys.every((k) => keys.includes(k)))
				return keys.map((k) => ALL_NAV_ITEMS.find((i) => i.key === k)!);
		}
	} catch {}
	return [...ALL_NAV_ITEMS];
}

const navItems = ref(loadNavOrder());

function saveOrder() {
	localStorage.setItem(LS_ORDER_KEY, JSON.stringify(navItems.value.map((i) => i.key)));
}

const route = useRoute();

const openSub = ref(false);
const showEditorPicker = ref(false);
const showDataPicker = ref(false);
const toast = useToast();

const tourLoading = ref(false);
const sidebarSeen = ref(false);

// const startTour = inject<() => void>("startTour")!;
const startPageTour = inject<() => Promise<boolean>>("startPageTour");
const canStartPageTour = inject<any>("canStartPageTour");

// const sidebarTourRef = ref<any>(null);
const tours = inject<Record<string, any>>("tours")!;
function handleDeviceClick() {
	if (!props.isOpen) return;
	openSub.value = !openSub.value;
}

/* ---- tour seen helpers ---- */
async function isSidebarSeen() {
	if (!authIsCached()) return true;
	if (sidebarSeen.value) return true;
	try {
		const { data } = await axios.get("/auth/me/tour", {
			params: { tour: "sidebar", page: PAGE },
		});
		sidebarSeen.value = !!data.seen;
	} catch (e) {
		console.error("[sidebar-tour] GET errore:", e);
		sidebarSeen.value = true;
	}
	return sidebarSeen.value;
}
async function markSidebarSeen() {
	if (!authIsCached() || sidebarSeen.value) return;
	try {
		await axios.post("/auth/me/tour", { tour: "sidebar", page: PAGE });
		sidebarSeen.value = true;
	} catch (e) {
		console.error("[sidebar-tour] POST errore:", e);
	}
}
/** Avvio automatico (first time) */
async function startTourIfNeeded() {
	if (!authIsCached()) return;
	if (await isSidebarSeen()) return;

	// Evita auto-tour in Account
	if (route.name === "account") return;

	// apri submenu e calcola step reali
	openSub.value = true;
	await nextTick();
	computedSidebarSteps.value = getVisibleSidebarSteps();
	if (!computedSidebarSteps.value.length) return;

	const tour = tours?.sidebarTour;
	if (tour?.start) tour.start();
}
function authReady() {
	startTourIfNeeded();
}

/* tour callbacks */
const RAW_SIDEBAR_STEPS = [
	{
		target: ".ico-home",
		content: "Vai alla tua dashboard principale",
		params: { placement: "right" },
	},
	{
		target: ".ico-devices",
		content: "Gestisci tutti i tuoi dispositivi",
		params: { placement: "right" },
	},
	{
		target: ".ico-live-maps",
		content: "Mappa live a tutto schermo",
		params: { placement: "right" },
	},
	{
		target: ".ico-editor",
		content: "Apri l’Editor del device",
		params: { placement: "right" },
	},
	{
		target: ".ico-data",
		content: "Consulta i dati live",
		params: { placement: "right" },
	},
	{
		target: ".ico-account",
		content: "Consulta la pagina del tuo account",
		params: { placement: "right" },
	},
	{
		target: ".ico-alerts",
		content: "Visualizza tutti gli avvisi del sistema",
		params: { placement: "right" },
	},
	{
		target: ".ico-help",
		content: "Riapri l’onboarding quando vuoi",
		params: { placement: "right" },
	},
] as const;

/** Filtra gli step che hanno il target presente nel DOM */
function getVisibleSidebarSteps(): any[] {
	return RAW_SIDEBAR_STEPS.filter((s) => !!document.querySelector(s.target));
}

/** Computed usato sia per bottone sia per avvio */
const computedSidebarSteps = ref<any[]>([]);
const canStartSidebarTour = computed(
	() => computedSidebarSteps.value.length > 0,
);

const canStartHelp = computed(() => {
	const pageOk = !!canStartPageTour?.value;
	return pageOk || canStartSidebarTour.value;
});

async function startHelp() {
	// 1) prova ad avviare la tour "global" della pagina
	try {
		if (startPageTour) {
			const started = await startPageTour();
			if (started) return; // è partita la tour della pagina ✅
			// non è partita: avviso e fallback alla sidebar
			const pageName =
				typeof route.name === "string"
					? route.name.charAt(0).toUpperCase() + route.name.slice(1)
					: "questa pagina";
			toast.info(
				`Nessuna guida disponibile per ${pageName}. Apro la guida relativa alla barra laterale…`,
				{
					position: "top-right",
					duration: 2500,
				},
			);
		}
	} catch (e) {
		console.warn("[help] startPageTour fallita:", e);
	}

	// 2) fallback → tour della sidebar
	await startSidebarTour();

	// 3) opzionale: se anche la sidebar non parte (nessuno step visibile)
	if (!canStartSidebarTour.value) {
		toast.warning("Nessuna guida disponibile in questa vista.", {
			position: "top-right",
			duration: 2500,
		});
	}
}

/** Avvio manuale dal bottone */
async function startSidebarTour() {
	// blocca in Account
	// if (route.name === "account") {
	// 	toast.info("Nessuna guida disponibile in questa pagina.");
	// 	return;
	// }

	// apri submenu per rendere visibili .ico-editor / .ico-data
	if (!openSub.value) openSub.value = true;
	await nextTick();

	// calcola step realmente presenti nel DOM
	computedSidebarSteps.value = getVisibleSidebarSteps();
	if (!computedSidebarSteps.value.length) {
		toast.info("Nessuna guida disponibile in questa pagina.");
		return;
	}

	// manager a nome: tours.sidebarTour
	const tour = tours?.sidebarTour;
	if (!tour || typeof tour.start !== "function") {
		console.warn("[sidebar-tour] manager non disponibile:", tours);
		toast.error("Impossibile avviare la guida in questo momento.");
		return;
	}

	// assicura che il prop :steps sia propagato
	await nextTick();
	try {
		tour.start();
	} catch (e) {
		console.error("[sidebar-tour] errore start():", e);
		toast.error("Si è verificato un problema nell'avvio della guida.");
	}
}

const sidebarCallbacks = {
	onStart() {
		tourLoading.value = true;
		nextTick(() => (tourLoading.value = false));
	},
	onFinish() {
		markSidebarSeen();
		tourLoading.value = false;
	},
	onSkip() {
		markSidebarSeen();
		tourLoading.value = false;
	},
	onError() {
		tourLoading.value = false;
	},
};

async function handleLogout() {
	try {
		const response = await axios.post("/auth/logout");
		if (response.status === 200) {
			setAuth(false);
			sidebarSeen.value = false;
			sessionStorage.removeItem("auth.me");
			toast.info(response.data || "Logout avvenuto con successo", {
				position: "top-right",
				duration: 2000,
			});
			setTimeout(() => router.push("/auth"), 500);
		}
	} catch (error) {
		console.error("Errore durante il logout:", error);
		toast.error("Errore durante il logout. Riprova.", {
			position: "top-right",
			duration: 5000,
		});
	}
}

onMounted(() => {
	// calcola subito se ci sono step visibili (per disabilitare il bottone)
	nextTick(() => (computedSidebarSteps.value = getVisibleSidebarSteps()));
	startTourIfNeeded();
	window.addEventListener("auth-ready", authReady);
});

// aggiorna gli step quando cambia rotta o cambia stato open/sub
watch(
	() => [route.fullPath, props.isOpen, openSub.value],
	() => nextTick(() => (computedSidebarSteps.value = getVisibleSidebarSteps())),
	{ flush: "post" },
);

// ferma tour al cambio pagina (extra safety)
watch(
	() => route.fullPath,
	() => {
		const tour = tours?.sidebarTour;
		tour?.stop?.();
		tourLoading.value = false;
	},
	{ flush: "post" },
);

onBeforeUnmount(() => {
	window.removeEventListener("auth-ready", authReady);
	const tour = tours?.sidebarTour;
	tour?.stop?.();
	tourLoading.value = false;
});

return (_ctx: any,_cache: any) => {
  const _component_v_tour = _resolveComponent("v-tour")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (tourLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [...(_cache[7] || (_cache[7] = [
          _createElementVNode("div", { class: "tour-spinner" }, null, -1)
        ]))]))
      : _createCommentVNode("", true),
    _createElementVNode("aside", {
      class: _normalizeClass(['sidebar', { open: __props.isOpen, desk: __props.desktop }])
    }, [
      _createElementVNode("header", _hoisted_2, [
        _createVNode(_unref(RouterLink), {
          to: "/devices",
          class: "logo"
        }, {
          default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
            _createElementVNode("img", {
              src: _imports_0,
              alt: "Archimede"
            }, null, -1)
          ]))]),
          _: 1
        }),
        (__props.desktop && __props.isOpen)
          ? (_openBlock(), _createBlock(NotificationBell, {
              key: 0,
              placement: "sidebar"
            }))
          : _createCommentVNode("", true),
        _createElementVNode("button", {
          class: "tgl",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('toggle'))),
          title: __props.isOpen ? 'Chiudi menu' : 'Apri menu'
        }, _toDisplayString(__props.isOpen ? "×" : "☰"), 9, _hoisted_3)
      ]),
      _createElementVNode("nav", _hoisted_4, [
        _createVNode(_unref(draggable), {
          modelValue: navItems.value,
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((navItems).value = $event)),
          "item-key": "key",
          animation: 150,
          onEnd: saveOrder
        }, {
          item: _withCtx(({ element }) => [
            _createElementVNode("div", _hoisted_5, [
              (element.key === 'devices')
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createVNode(_unref(RouterLink), {
                      to: "/devices",
                      class: _normalizeClass(['item', element.cls]),
                      onClick: handleDeviceClick
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(), _createBlock(_resolveDynamicComponent(element.icon), { class: "ico" })),
                        _withDirectives(_createElementVNode("span", null, _toDisplayString(element.label), 513), [
                          [_vShow, __props.isOpen || !__props.desktop]
                        ]),
                        (__props.isOpen || !__props.desktop)
                          ? (_openBlock(), _createBlock(_unref(ChevronIcon), {
                              key: 0,
                              class: _normalizeClass({ rot: openSub.value })
                            }, null, 8, ["class"]))
                          : _createCommentVNode("", true)
                      ]),
                      _: 2
                    }, 1032, ["class"]),
                    (__props.isOpen && openSub.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                          _createElementVNode("button", {
                            class: "item sub-i ico-editor",
                            onClick: _cache[1] || (_cache[1] = ($event: any) => (showEditorPicker.value = true))
                          }, [
                            _createVNode(_unref(EditIcon), { class: "ico" }),
                            _cache[9] || (_cache[9] = _createElementVNode("span", null, "Editor", -1))
                          ]),
                          (showEditorPicker.value)
                            ? (_openBlock(), _createBlock(DevicePickerModal, {
                                key: 0,
                                target: "Editor",
                                onClose: _cache[2] || (_cache[2] = ($event: any) => (showEditorPicker.value = false))
                              }))
                            : _createCommentVNode("", true),
                          _createElementVNode("button", {
                            class: "item sub-i ico-data",
                            onClick: _cache[3] || (_cache[3] = ($event: any) => (showDataPicker.value = true))
                          }, [
                            _createVNode(_unref(DatabaseIcon), { class: "ico" }),
                            _cache[10] || (_cache[10] = _createElementVNode("span", null, "Data", -1))
                          ]),
                          (showDataPicker.value)
                            ? (_openBlock(), _createBlock(DevicePickerModal, {
                                key: 1,
                                target: "Data",
                                onClose: _cache[4] || (_cache[4] = ($event: any) => (showDataPicker.value = false))
                              }))
                            : _createCommentVNode("", true)
                        ]))
                      : _createCommentVNode("", true)
                  ], 64))
                : (_openBlock(), _createBlock(_unref(RouterLink), {
                    key: 1,
                    to: element.to,
                    class: _normalizeClass(['item', element.cls])
                  }, {
                    default: _withCtx(() => [
                      (_openBlock(), _createBlock(_resolveDynamicComponent(element.icon), { class: "ico" })),
                      _withDirectives(_createElementVNode("span", null, _toDisplayString(element.label), 513), [
                        [_vShow, __props.isOpen || !__props.desktop]
                      ])
                    ]),
                    _: 2
                  }, 1032, ["to", "class"]))
            ])
          ]),
          _: 1
        }, 8, ["modelValue"])
      ]),
      _createVNode(_unref(RouterLink), {
        to: "/account",
        class: "item ico-account"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(CircleUserIcon), { class: "ico" }),
          _withDirectives(_createElementVNode("span", null, "Account", 512), [
            [_vShow, __props.isOpen || !__props.desktop]
          ])
        ]),
        _: 1
      }),
      _createElementVNode("button", {
        class: "item ico-help",
        disabled: !canStartHelp.value,
        title: canStartHelp.value ? 'Guida rapida' : 'Nessuna guida in questa pagina',
        onClick: startHelp
      }, [
        _createVNode(_unref(HelpCircleIcon), { class: "ico" }),
        _withDirectives(_createElementVNode("span", null, "Guida rapida", 512), [
          [_vShow, __props.isOpen || !__props.desktop]
        ])
      ], 8, _hoisted_7),
      _createVNode(_unref(RouterLink), { to: "/logout" }, {
        default: _withCtx(() => [
          _createElementVNode("a", {
            class: "item logout",
            onClick: _withModifiers(handleLogout, ["prevent"])
          }, [
            _createVNode(_unref(LogOutIcon), { class: "ico" }),
            _withDirectives(_createElementVNode("span", null, "Logout", 512), [
              [_vShow, __props.isOpen || !__props.desktop]
            ])
          ])
        ]),
        _: 1
      })
    ], 2),
    (!__props.desktop && !__props.isOpen)
      ? (_openBlock(), _createElementBlock("button", {
          key: 1,
          class: "hamb",
          onClick: _cache[6] || (_cache[6] = ($event: any) => (_ctx.$emit('toggle'))),
          title: !__props.isOpen ? 'Apri menu' : 'Chiudi menu'
        }, " ☰ ", 8, _hoisted_8))
      : _createCommentVNode("", true),
    _createVNode(_component_v_tour, {
      name: "sidebarTour",
      steps: computedSidebarSteps.value,
      callbacks: sidebarCallbacks
    }, null, 8, ["steps"])
  ], 64))
}
}

})